import React from "react";
import {
  alpha,
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import MapComponent from "../map/MapComponent";
import PatternPreview from "./PatternPreview";
import BatteryForm from "./BatteryForm";
import CreateFireMissionTool from "./CreateFireMissionTool";
import EditFireMissionTool from "./EditFireMissionTool";
import SafetyClearanceTool from "./SafetyClearanceTool";
import { azimuthMilsFromVector, dist, f0 } from "../../utils/fdcHelpers";

export default function ToolsDock(props) {
  const {
    toolsOpen,
    startResize,
    dockWidth,
    activeTool,
    showMapPanel,
    showPatternPanel,
    showBatteryTool,
    showSafetyTool,
    showFireRegTool,
    showCreateMissionTool,
    showEditMissionTool,
    shots,
    form,
    mapCenter,
    processedObservations,
    observations,
    batteryOptions,
    backendUrl,
    gunOptions,
    fetchBatteryData,
    correctionMode,
    setCorrectionMode,
    regulatingGunId,
    setRegulatingGunId,
    patternAnchor,
    setPatternAnchor,
    effectiveTargetMgrs,
    setEffectiveTargetMgrs,
    patternEnabled,
    setPatternEnabled,
    getCenterTargetMgrs,
    patternMode,
    setPatternMode,
    buildCircleDistribution,
    buildLinearDistribution,
    setPatternPoints,
    patternPoints,
    patternRadius,
    setPatternRadius,
    patternRotationMils,
    setPatternRotationMils,
    linearBearingMils,
    setLinearBearingMils,
    linearSpacing,
    setLinearSpacing,
    linearSnapToTicks,
    setLinearSnapToTicks,
    ringStep,
    setRingStep,
    radialStepMils,
    setRadialStepMils,
    showRadialLabels,
    setShowRadialLabels,
    snapEnabled,
    setSnapEnabled,
    snapMeters,
    setSnapMeters,
    patternError,
    selectedShotId,
    setSelectedShotId,
    selectedProcessedObservationId,
    setSelectedProcessedObservationId,
    selectedGunId,
    setSelectedGunId,
    createMissionDefaultZone,
    handleMissionCreated,
    handleMissionUpdated,
  } = props;

  const theme = useTheme();
  const tacticalPaperSx = {
    
    border: "1px solid",
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(166,176,120,0.22)"
        : "rgba(85,107,47,0.14)",
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(21,27,17,0.96)"
        : "rgba(250,251,246,0.98)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 12px 28px rgba(0,0,0,0.24)"
        : "0 12px 28px rgba(51,61,36,0.08)",
  };

  const tacticalHeaderSx = {
    px: 2,
    py: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid",
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(166,176,120,0.16)"
        : "rgba(85,107,47,0.12)",
    background:
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg, rgba(41,52,31,0.46), rgba(24,30,19,0.14))"
        : "linear-gradient(180deg, rgba(125,141,88,0.14), rgba(125,141,88,0.03))",
  };

  const tacticalOutlinedButtonSx = {
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 600,
    letterSpacing: 0.2,
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(166,176,120,0.55)"
        : "rgba(85,107,47,0.55)",
    color: theme.palette.mode === "dark" ? "#c7d1a7" : "#465a27",
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(41,52,31,0.28)"
        : "rgba(125,141,88,0.08)",
    "&:hover": {
      borderColor: theme.palette.mode === "dark" ? "#a6b078" : "#556b2f",
      bgcolor:
        theme.palette.mode === "dark"
          ? "rgba(63,79,47,0.40)"
          : "rgba(125,141,88,0.16)",
    },
  };

  if (!toolsOpen) return null;

  return (
    <>
      <Box
        onMouseDown={startResize}
        sx={{
          display: { xs: "none", md: "block" },
          width: 8,
          cursor: "col-resize",
          position: "relative",
          flex: "0 0 auto",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 2,
            bgcolor: "divider",
            borderRadius: 999,
          },
          "&:hover::before": {
            bgcolor: "primary.main",
          },
        }}
      />

      <Box
        sx={{
          width: { xs: "100%", md: `${dockWidth}px` },
          flex: { xs: "1 1 100%", md: "0 0 auto" },
          minWidth: 0,
          minHeight: { xs: 0, md: 0 },
          height: { xs: "auto", md: "100%" },
          overflowY: { xs: "visible", md: "auto" },
          overflowX: "hidden",
          pl: { xs: 0, md: 1 },
          mt: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            minHeight: { xs: 0, md: "100%" },
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {showMapPanel && (
            <Paper
              elevation={0}
              sx={{
                flex: "0 0 auto",
                height: 700,
                minHeight: 700,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Mission Map</Typography>
              </Box>
              <Box sx={{ height: 700, minHeight: 700, flex: "0 0 auto" }}>
                <MapComponent
                  mapId="side-map"
                  shots={shots}
                  mgrsZone={form.mgrs_zone}
                  btyCenter={form.bty_center}
                  mapCenter={mapCenter}
                  processedObservations={processedObservations}
                  unprocessedObservations={observations}
                  targetLocation={form.mgrs_target}
                  batteryOptions={batteryOptions}
                  selectedShotId={selectedShotId}
                  onShotSelect={setSelectedShotId}
                  selectedProcessedObservationId={selectedProcessedObservationId}
                  onProcessedObservationSelect={setSelectedProcessedObservationId}
                  selectedGunId={selectedGunId}
                  onGunSelect={setSelectedGunId}
                />
              </Box>
            </Paper>
          )}

          {showPatternPanel && (
            <Paper
              elevation={0}
              sx={{
                flex: activeTool === "pattern" ? "1 1 auto" : "0 0 auto",
                minHeight: 500,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Pattern Editor</Typography>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox
                      checked={patternEnabled}
                      onChange={(e) => setPatternEnabled(e.target.checked)}
                    />
                    <Typography>Enable per-gun offsets</Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Center: <b>{getCenterTargetMgrs() || "—"}</b>
                  </Typography>

                  <ToggleButtonGroup
                    value={patternMode}
                    exclusive
                    onChange={(_e, v) => {
                      if (!v) return;
                      setPatternMode(v);
                      if (v === "circle" && batteryOptions.length) {
                        setPatternPoints((prev) => ({
                          ...prev,
                          ...buildCircleDistribution(batteryOptions),
                        }));
                      }
                      if (v === "linear" && batteryOptions.length) {
                        setPatternPoints((prev) => ({
                          ...prev,
                          ...buildLinearDistribution(batteryOptions),
                        }));
                      }
                    }}
                    size="small"
                  >
                    <ToggleButton value="free">Free</ToggleButton>
                    <ToggleButton value="circle">Circle</ToggleButton>
                    <ToggleButton value="linear">Linear</ToggleButton>
                  </ToggleButtonGroup>

                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => setPatternPoints({})}
                    disabled={!batteryOptions.length}
                    sx={tacticalOutlinedButtonSx}
                  >
                    Reset Points
                  </Button>
                </Box>

                {patternMode === "circle" && (
                  <Box
                    sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}
                  >
                    <TextField
                      label="Radius (m)"
                      type="number"
                      size="small"
                      value={patternRadius}
                      onChange={(e) =>
                        setPatternRadius(Number(e.target.value) || 0)
                      }
                      sx={{ width: 140 }}
                    />
                    <TextField
                      label="Rotation (mils)"
                      type="number"
                      size="small"
                      value={patternRotationMils}
                      onChange={(e) =>
                        setPatternRotationMils(Number(e.target.value) || 0)
                      }
                      sx={{ width: 160 }}
                    />
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setPatternPoints((prev) => ({
                          ...prev,
                          ...buildCircleDistribution(batteryOptions),
                        }))
                      }
                      disabled={!batteryOptions.length}
                      sx={tacticalOutlinedButtonSx}
                    >
                      Distribute Circle
                    </Button>
                  </Box>
                )}

                {patternMode === "linear" && (
                  <Box
                    sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}
                  >
                    <TextField
                      label="Bearing (mils)"
                      type="number"
                      size="small"
                      value={linearBearingMils}
                      onChange={(e) =>
                        setLinearBearingMils(Number(e.target.value) || 0)
                      }
                      sx={{ width: 160 }}
                    />
                    <TextField
                      label="Spacing (m)"
                      type="number"
                      size="small"
                      value={linearSpacing}
                      onChange={(e) =>
                        setLinearSpacing(Number(e.target.value) || 0)
                      }
                      sx={{ width: 140 }}
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        checked={linearSnapToTicks}
                        onChange={(e) => setLinearSnapToTicks(e.target.checked)}
                      />
                      <Typography>Snap to ticks</Typography>
                    </Box>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setPatternPoints((prev) => ({
                          ...prev,
                          ...buildLinearDistribution(batteryOptions),
                        }))
                      }
                      disabled={!batteryOptions.length}
                      sx={tacticalOutlinedButtonSx}
                    >
                      Distribute Line
                    </Button>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <TextField
                    label="Ring step (m)"
                    type="number"
                    size="small"
                    value={ringStep}
                    onChange={(e) => setRingStep(Number(e.target.value) || 50)}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    label="Radial step (mils)"
                    type="number"
                    size="small"
                    value={radialStepMils}
                    onChange={(e) =>
                      setRadialStepMils(Number(e.target.value) || 200)
                    }
                    sx={{ width: 170 }}
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox
                      checked={showRadialLabels}
                      onChange={(e) => setShowRadialLabels(e.target.checked)}
                    />
                    <Typography>Labels</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox
                      checked={snapEnabled}
                      onChange={(e) => setSnapEnabled(e.target.checked)}
                    />
                    <Typography>Snap</Typography>
                  </Box>
                  <TextField
                    label="Snap (m)"
                    type="number"
                    size="small"
                    value={snapMeters}
                    onChange={(e) => setSnapMeters(Number(e.target.value) || 5)}
                    sx={{ width: 120 }}
                    disabled={!snapEnabled}
                  />
                </Box>

                {patternError && (
                  <Typography sx={{ mt: 2, color: "error.main" }}>
                    {patternError}
                  </Typography>
                )}

                {batteryOptions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Pattern Preview
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        overflowX: "auto",
                        overflowY: "hidden",
                        WebkitOverflowScrolling: "touch",
                      }}
                    >
                      <PatternPreview
                        guns={batteryOptions}
                        points={patternPoints}
                        setPoints={setPatternPoints}
                        mode={patternMode}
                        enabled={patternEnabled}
                        radiusMeters={patternRadius}
                        bearingMils={linearBearingMils}
                        spacingMeters={linearSpacing}
                        snapToTicks={linearSnapToTicks}
                        ringStepMeters={ringStep}
                        radialStep={radialStepMils}
                        showLabels={showRadialLabels}
                        snapEnabled={snapEnabled}
                        snapMeters={snapMeters}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              {batteryOptions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Per-gun offsets (relative to center)
                  </Typography>
                  {batteryOptions.map((g) => {
                    const gunId = String(g.gun_number);
                    const p = patternPoints[gunId] || { dx: 0, dy: 0 };
                    const r = dist(p.dx, p.dy);
                    const az = azimuthMilsFromVector(p.dx, p.dy);
                    return (
                      <Typography
                        key={`off-${gunId}`}
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {`Gun ${gunId}: ΔE ${f0(p.dx)}m, ΔN ${f0(p.dy)}m | Dist ${f0(r)}m | Az ${String(Math.round(az)).padStart(4, "0")} mil`}
                      </Typography>
                    );
                  })}
                </Box>
              )}
            </Paper>
          )}

          {showCreateMissionTool && (
            <Paper
              elevation={0}
              sx={{
                flex: activeTool === "createMission" ? "1 1 auto" : "0 0 auto",
                minHeight: 420,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Create Fire Mission</Typography>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
                <CreateFireMissionTool
                  backendUrl={backendUrl}
                  defaultMgrsZone={createMissionDefaultZone}
                  onCreated={handleMissionCreated}
                />
              </Box>
            </Paper>
          )}

          {showEditMissionTool && (
            <Paper
              elevation={0}
              sx={{
                flex: activeTool === "editMission" ? "1 1 auto" : "0 0 auto",
                minHeight: 420,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Edit Fire Mission</Typography>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
                <EditFireMissionTool
                  backendUrl={backendUrl}
                  missionForm={form}
                  defaultMgrsZone={createMissionDefaultZone}
                  onUpdated={handleMissionUpdated}
                />
              </Box>
            </Paper>
          )}

          {showBatteryTool && (
            <Paper
              elevation={0}
              sx={{
                flex: activeTool === "battery" ? "1 1 auto" : "0 0 auto",
                minHeight: 360,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Gun Position</Typography>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
                <BatteryForm
                  backendUrl={backendUrl}
                  missionId={form.mission_id}
                  gunOptions={gunOptions}
                  onSaved={fetchBatteryData}
                />
              </Box>
            </Paper>
          )}

          {showFireRegTool && (
            <Paper
              elevation={0}
              sx={{
                flex: activeTool === "fireReg" ? "1 1 auto" : "0 0 auto",
                minHeight: 320,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  Fire Regulation Mode
                </Typography>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  <ToggleButtonGroup
                    value={correctionMode}
                    exclusive
                    onChange={(e, v) => v && setCorrectionMode(v)}
                    size="small"
                  >
                    <ToggleButton value="single">
                      Single-gun regulation
                    </ToggleButton>
                    <ToggleButton value="multi">
                      Per-gun corrections
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel id="reg-gun-label">Regulating Gun</InputLabel>
                    <Select
                      labelId="reg-gun-label"
                      value={regulatingGunId ?? ""}
                      label="Regulating Gun"
                      onChange={(e) => setRegulatingGunId(e.target.value)}
                      disabled={!batteryOptions.length}
                    >
                      {batteryOptions.map((g) => (
                        <MenuItem
                          key={g.id}
                          value={g.id}
                        >{`Gun ${g.gun_number} (MGRS: ${g.mgrs})`}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel id="pattern-anchor-label">
                      Pattern Anchor
                    </InputLabel>
                    <Select
                      labelId="pattern-anchor-label"
                      value={patternAnchor}
                      label="Pattern Anchor"
                      onChange={(e) => setPatternAnchor(e.target.value)}
                    >
                      <MenuItem value="original">Original Target</MenuItem>
                      <MenuItem
                        value="effective"
                        disabled={!effectiveTargetMgrs}
                      >
                        Effective Target (False Target)
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setEffectiveTargetMgrs("");
                      setPatternAnchor("original");
                    }}
                    disabled={!effectiveTargetMgrs}
                    sx={tacticalOutlinedButtonSx}
                  >
                    Clear Effective Target
                  </Button>
                </Box>

                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Effective Target: <b>{effectiveTargetMgrs || "— not set yet —"}</b>
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {showSafetyTool && (
            <Paper
              elevation={0}
              sx={{
                flex: activeTool === "safety" ? "1 1 auto" : "0 0 auto",
                minHeight: 320,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>Safety Clearance</Typography>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
                <SafetyClearanceTool backendUrl={backendUrl} missionId={form?.mission_id} />
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    </>
  );
}
